import type { StaticSuggestions } from '~/types/address-context';
import type { Addresses } from '~~/core/entities/Addresses';

import { CityDataRegistry } from '~/config/cities';

/**
 * Composable para acessar o City Registry
 * Facilita o uso do registry em componentes e composables
 */
export function useCityRegistry() {
	const registry = CityDataRegistry.getInstance();

	/**
	 * Verifica se um termo de busca está relacionado a alguma cidade registrada
	 */
	function isCityRelatedSearch(searchTerm: string): boolean {
		return registry.findCityBySearchTerm(searchTerm) !== undefined;
	}

	/**
	 * Retorna a cidade relacionada a um termo de busca
	 */
	function findCityBySearchTerm(searchTerm: string) {
		return registry.findCityBySearchTerm(searchTerm);
	}

	/**
	 * Retorna a cidade por estado
	 */
	function findCityByState(state: string) {
		return registry.findCityByState(state);
	}

	/**
	 * Retorna todas as cidades registradas
	 */
	function getAllCities() {
		return registry.getAllCities();
	}

	/**
	 * Retorna a cidade padrão
	 */
	function getDefaultCity() {
		return registry.getDefaultCity();
	}

	/**
	 * Retorna sugestões estáticas para uma busca
	 * Se houver uma cidade relacionada, retorna suas sugestões
	 */
	function getStaticSuggestions(searchTerm: string): StaticSuggestions | null {
		const city = registry.findCityBySearchTerm(searchTerm);
		return city?.getStaticSuggestions(searchTerm) ?? null;
	}

	/**
	 * Mescla dados de endereços da API com dados estáticos das cidades
	 */
	function getMergedAddresses(fetchedAddresses: Addresses[]): Addresses[] {
		return registry.getMergedAddresses(fetchedAddresses);
	}

	/**
	 * Retorna todos os estados disponíveis
	 */
	function getAllStates(): string[] {
		return registry.getAllStates();
	}

	return {
		registry,
		isCityRelatedSearch,
		findCityBySearchTerm,
		findCityByState,
		getAllCities,
		getDefaultCity,
		getStaticSuggestions,
		getMergedAddresses,
		getAllStates
	};
}
